import type {CIFinding, CIFindingCheckRun, DedupedCIFinding} from './ci-findings-types'

function checkRunForFinding(finding: CIFinding): CIFindingCheckRun {
  return {
    id: finding.failedCheckRunId,
    ...(finding.failedCheckRunName ? {name: finding.failedCheckRunName} : {}),
    ...(finding.failedCheckRunUrl ? {url: finding.failedCheckRunUrl} : {}),
  }
}

function mergeCheckRunMetadata(firstSeen: CIFindingCheckRun, additional: CIFindingCheckRun): CIFindingCheckRun {
  const name = firstSeen.name || additional.name
  const url = firstSeen.url || additional.url

  return {
    id: firstSeen.id,
    ...(name && {name}),
    ...(url && {url}),
  }
}

function uniqueCheckRuns(findings: CIFinding[]): CIFindingCheckRun[] {
  const checkRuns = new Map<number, CIFindingCheckRun>()

  for (const finding of findings) {
    const checkRun = checkRunForFinding(finding)
    const existing = checkRuns.get(checkRun.id)
    checkRuns.set(checkRun.id, existing ? mergeCheckRunMetadata(existing, checkRun) : checkRun)
  }

  return [...checkRuns.values()]
}

/**
 * Groups findings by `dedupKey` and returns one representative per group.
 * Findings without a dedupKey are treated as unique (never collapsed).
 * Preserves the order of first appearance.
 */
export function deduplicateCIFindings(findings: CIFinding[]): DedupedCIFinding[] {
  const groups = new Map<string, CIFinding[]>()
  const result: DedupedCIFinding[] = []

  for (const finding of findings) {
    const key = finding.dedupKey
    if (!key) {
      result.push({
        ...finding,
        dedupCount: 1,
        dedupedFindingIds: [finding.id],
        checkRuns: [checkRunForFinding(finding)],
      })
      continue
    }

    const existing = groups.get(key)
    if (existing) {
      existing.push(finding)
    } else {
      const group = [finding]
      groups.set(key, group)
      result.push({
        ...finding,
        dedupCount: 0,
        dedupedFindingIds: [],
        checkRuns: [],
      })
    }
  }

  // Finalize grouped entries with their totals.
  for (const deduped of result) {
    const key = deduped.dedupKey
    if (!key) continue

    const group = groups.get(key)
    if (!group) continue

    deduped.dedupCount = group.length
    deduped.dedupedFindingIds = group.map(f => f.id)
    deduped.checkRuns = uniqueCheckRuns(group)
  }

  return result
}
